<template>
	<div
		ref="radio-group"
		:class="`radio-group radio-group--${groupStyle} radio-group--${size}`"
		role="radiogroup"
	>
		<div
			v-for="(radio, index) in radios"
			:key="index"
			:class="{ 'is-selected' : radio.value === value }"
			class="radio-group__radio-button"
			data-cy="radio-group-radio-button"
			@click="handleChange(radio.value)"
			@mouseover="currentHover = index"
			@mouseleave="currentHover = null"
		>
			<div
				v-if="$scopedSlots.prepend && groupStyle === 'list'"
				class="radio-group__prepend"
			>
				<slot
					:radio="radio"
					name="prepend"
				/>
			</div>

			<helio-radio
				ref="radio-button"
				:label="radio.label"
				:icon-token="radio.iconToken ? radio.iconToken : null"
				:icon-weight="radio.iconWeight ? radio.iconWeight : 'regular'"
				:value="radio.value"
				:initial-model="radio.initialModel"
				:disabled="radio.disabled ? radio.disabled : false"
				:hover="currentHover === index"
				@changed="changeValue"
			/>

			<p
				v-if="radio.text && groupStyle === 'tile'"
				v-html="radio.text"
			/>
		</div>
	</div>
</template>

<script>
import Radio from '../radio/Radio.vue';

export default {
	name: 'RadioGroup',

	components: {
		'helio-radio': Radio,
	},

	props: {
		groupStyle: {
			type: String,
			required: false,
			default: 'tile',
			validator: value => ['tile', 'list'].indexOf(value) !== -1,
			description: 'Sets which preset style will be applied to the RadioGroup',
		},

		mediumSize: {
			type: Number,
			required: false,
			default: 719,
			description: 'Sets the breakpoint where the medium layout should be applied',
		},

		radios: {
			type: Array,
			required: true,
			description: 'Supplies data for each of the radio buttons in the RadioGroup',
		},

		smallSize: {
			type: Number,
			required: false,
			default: 552,
			description: 'Sets the breakpoint where the small layout should be applied',
		},
	},

	slots: {
		prepend: {
			required: false,
			scoped: true,
			description: 'Supplies the content that will be added to the left-hand side of the list-style RadioGroup (e.g., an avatar)',
		},
	},

	data() {
		return {
			size: 'medium',
			value: null,
			currentHover: null,
		};
	},

	watch: {
		radios(newRadios) {
			if (this.radios.length > 0) {
				const selectedRadio = newRadios.find(radio => radio.initialModel === radio.value);

				if (selectedRadio && selectedRadio.value) {
					this.value = selectedRadio.value;
					this.handleChange(selectedRadio.value);
				} else {
					this.value = '';
				}
			} else {
				this.value = '';
			}
		},
	},

	mounted() {
		const selectedRadio = this.radios.find(radio => radio.initialModel === radio.value);

		if (selectedRadio && selectedRadio.value) {
			this.value = selectedRadio.value;
		}

		this.onResize();
		window.addEventListener('resize', this.onResize);
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.$nextTick(() => {
				const radioGroup = this.$refs['radio-group'];

				if (radioGroup) {
					if (radioGroup.clientWidth <= this.smallSize) {
						this.size = 'small';
					} else if (radioGroup.clientWidth <= this.mediumSize) {
						this.size = 'medium';
					} else {
						this.size = 'large';
					}
				}
			});
		},

		handleChange(value) {
			const radios = this.$refs['radio-button'];

			if (this.radios && radios) {
				const selected = radios.filter(radio => radio.value === value);
				selected[0].$vnode.elm.click();
			}
		},

		changeValue(value) {
			const radios = this.$refs['radio-button'];

			if (this.radios) {
				const notSelected = radios.filter(radio => radio.value !== value);
				notSelected.forEach((radio) => {
					radio.checked = false;
					radio.ariaChecked = false;
				});

				this.value = value;

				this.$emit('selected', value);
			}
		},
	},
};
</script>

<style lang="scss" src="./radio-group.scss" />
